import { Button, Chip, Group, Menu, Modal, MultiSelect, Select, Stack, Text, TextInput } from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { UNASSIGNED_ASSIGNEE, type GroupKey, type SortKey, type TicketView, type TicketViewResult } from "../../lib/customerView/ticketView.js";
import { applyPreset, PRESET_NAME_MAX, presetMatches } from "../../lib/customerView/viewPresets.js";
import { useViewPresetsStore } from "../../stores/viewPresets.js";

const SEARCH_DEBOUNCE_MS = 250;

interface Props {
  view: TicketView;
  onChange: (next: TicketView) => void;
  counts: TicketViewResult["counts"];
}

/**
 * Filter/Sortierung/Gruppierung über die geladene Menge. Status- und Typ-Chips
 * sind AUSSCHLUSS-Listen (angehakt = sichtbar), Ersteller und Bearbeiter sind
 * Auswahlen — die typische Frage ist „alles von X und Y", nicht „X ausblenden".
 *
 * Presets (rechts): Die ganze Leiste außer der Schnellsuche unter einem Namen
 * speichern und mit einem Klick wieder anwenden — je Browser, s.
 * `lib/customerView/viewPresets.ts`.
 */
export function TicketToolbar({ view, onChange, counts }: Props) {
  const { t } = useTranslation();
  const presets = useViewPresetsStore((s) => s.presets);
  const savePreset = useViewPresetsStore((s) => s.save);
  const removePreset = useViewPresetsStore((s) => s.remove);
  const [saveOpen, setSaveOpen] = useState(false);
  const [presetName, setPresetName] = useState("");
  const submitPreset = () => {
    const saved = savePreset(presetName, view);
    if (!saved) return;
    notifications.show({ message: t("pages.customerView.cockpit.presetSaved", { name: saved.name }) });
    setPresetName("");
    setSaveOpen(false);
  };
  const statuses = Object.keys(counts.status).sort((a, b) => a.localeCompare(b, "de"));
  const types = Object.keys(counts.type).sort((a, b) => a.localeCompare(b, "de"));
  const reporters = Object.keys(counts.reporter).sort((a, b) => a.localeCompare(b, "de"));
  // „Nicht zugewiesen" steht unten und nur, wenn es solche Vorgänge gibt —
  // ein Eintrag, der garantiert nichts trifft, ist eine Falle, keine Hilfe.
  const assigneeOptions = [
    ...Object.keys(counts.assignee)
      .sort((a, b) => a.localeCompare(b, "de"))
      .map((a) => ({ value: a, label: `${a} (${counts.assignee[a]})` })),
    ...(counts.unassigned > 0
      ? [{ value: UNASSIGNED_ASSIGNEE, label: `${t("pages.customerView.cockpit.unassigned")} (${counts.unassigned})` }]
      : []),
  ];

  const shownStatus = statuses.filter((s) => !view.excludedStatus.includes(s));
  const shownTypes = types.filter((s) => !view.excludedTypes.includes(s));

  // Eigener Zustand für das Eingabefeld, damit jeder Tastendruck sofort sichtbar
  // ist; erst nach kurzer Ruhe wandert der Text in die URL (und löst damit
  // Filterung/Navigation aus). Von außen geänderte q (z. B. Kundenwechsel,
  // Filter-Reset) übernimmt das Feld sofort.
  // Der zuletzt selbst in die URL geschobene Wert: Kommt genau der zurück,
  // ist es keine äußere Änderung, und ein inzwischen weitergetippter Text
  // darf nicht überschrieben werden.
  const [searchText, setSearchText] = useState(view.q);
  const lastPushed = useRef(view.q);
  useEffect(() => {
    if (view.q !== lastPushed.current) {
      lastPushed.current = view.q;
      setSearchText(view.q);
    }
  }, [view.q]);
  const [debouncedSearch] = useDebouncedValue(searchText, SEARCH_DEBOUNCE_MS);
  useEffect(() => {
    if (debouncedSearch !== view.q) {
      lastPushed.current = debouncedSearch;
      onChange({ ...view, q: debouncedSearch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  return (
    <Group gap="lg" wrap="wrap" align="flex-end">
      <TextInput
        size="xs"
        w={220}
        placeholder={t("pages.customerView.cockpit.search")}
        value={searchText}
        onChange={(e) => setSearchText(e.currentTarget.value)}
      />
      {statuses.length > 0 && (
        <Group gap="xs">
          <Text size="xs" c="dimmed">{t("pages.customerView.cockpit.status")}</Text>
          <Chip.Group multiple value={shownStatus} onChange={(shown) => onChange({ ...view, excludedStatus: statuses.filter((s) => !shown.includes(s)) })}>
            <Group gap={4}>
              {statuses.map((s) => (
                <Chip key={s} value={s} size="xs" radius="sm">{s} ({counts.status[s]})</Chip>
              ))}
            </Group>
          </Chip.Group>
        </Group>
      )}
      {types.length > 0 && (
        <Group gap="xs">
          <Text size="xs" c="dimmed">{t("pages.customerView.cockpit.type")}</Text>
          <Chip.Group multiple value={shownTypes} onChange={(shown) => onChange({ ...view, excludedTypes: types.filter((s) => !shown.includes(s)) })}>
            <Group gap={4}>
              {types.map((s) => (
                <Chip key={s} value={s} size="xs" radius="sm">{s} ({counts.type[s]})</Chip>
              ))}
            </Group>
          </Chip.Group>
        </Group>
      )}
      {reporters.length > 0 && (
        <MultiSelect
          size="xs"
          w={240}
          clearable
          searchable
          label={t("pages.customerView.cockpit.reporter")}
          placeholder={t("pages.customerView.cockpit.allReporters")}
          value={view.reporters}
          onChange={(reporters) => onChange({ ...view, reporters })}
          data={reporters.map((r) => ({ value: r, label: `${r} (${counts.reporter[r]})` }))}
        />
      )}
      {assigneeOptions.length > 0 && (
        <MultiSelect
          size="xs"
          w={240}
          clearable
          searchable
          label={t("pages.customerView.cockpit.assigneeFilter")}
          placeholder={view.assignees.length === 0 ? t("pages.customerView.cockpit.allAssignees") : undefined}
          value={view.assignees}
          onChange={(assignees) => onChange({ ...view, assignees })}
          data={assigneeOptions}
        />
      )}
      <Select
        size="xs"
        w={190}
        label={t("pages.customerView.cockpit.sort")}
        value={view.sort}
        onChange={(v) => v && onChange({ ...view, sort: v as SortKey })}
        data={[
          { value: "updated", label: t("pages.customerView.cockpit.sortUpdated") },
          { value: "created", label: t("pages.customerView.cockpit.sortCreated") },
          { value: "status", label: t("pages.customerView.cockpit.status") },
          { value: "type", label: t("pages.customerView.cockpit.type") },
          { value: "key", label: t("pages.customerView.cockpit.sortKey") },
        ]}
      />
      <Select
        size="xs"
        w={160}
        label={t("pages.customerView.cockpit.group")}
        value={view.group}
        onChange={(v) => v && onChange({ ...view, group: v as GroupKey })}
        data={[
          { value: "none", label: t("pages.customerView.cockpit.groupNone") },
          { value: "status", label: t("pages.customerView.cockpit.status") },
          { value: "type", label: t("pages.customerView.cockpit.type") },
          { value: "reporter", label: t("pages.customerView.cockpit.reporter") },
        ]}
      />
      <Menu shadow="md" width={280} position="bottom-end" closeOnItemClick>
        <Menu.Target>
          <Button size="xs" variant="default">{t("pages.customerView.cockpit.presets")}</Button>
        </Menu.Target>
        <Menu.Dropdown>
          {presets.length === 0 && (
            <Menu.Label>{t("pages.customerView.cockpit.noPresets")}</Menu.Label>
          )}
          {presets.map((p) => (
            <Menu.Item
              key={p.id}
              fw={presetMatches(p, view) ? 700 : undefined}
              onClick={() => onChange(applyPreset(p, view))}
              rightSection={
                <Button
                  size="compact-xs"
                  variant="subtle"
                  color="red"
                  aria-label={t("pages.customerView.cockpit.presetDelete", { name: p.name })}
                  onClick={(e) => {
                    e.stopPropagation();
                    removePreset(p.id);
                  }}
                >
                  ×
                </Button>
              }
            >
              {p.name}
            </Menu.Item>
          ))}
          <Menu.Divider />
          <Menu.Item onClick={() => setSaveOpen(true)}>{t("pages.customerView.cockpit.savePreset")}</Menu.Item>
          <Menu.Label>{t("pages.customerView.cockpit.presetHint")}</Menu.Label>
        </Menu.Dropdown>
      </Menu>
      <Modal opened={saveOpen} onClose={() => setSaveOpen(false)} title={t("pages.customerView.cockpit.savePreset")} size="sm">
        <Stack gap="sm">
          <TextInput
            data-autofocus
            label={t("pages.customerView.cockpit.presetName")}
            value={presetName}
            maxLength={PRESET_NAME_MAX}
            onChange={(e) => setPresetName(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") submitPreset();
            }}
          />
          <Text size="xs" c="dimmed">{t("pages.customerView.cockpit.presetHint")}</Text>
          <Group justify="flex-end">
            <Button variant="default" size="xs" onClick={() => setSaveOpen(false)}>{t("common.cancel")}</Button>
            <Button size="xs" disabled={presetName.trim() === ""} onClick={submitPreset}>{t("pages.customerView.cockpit.presetSave")}</Button>
          </Group>
        </Stack>
      </Modal>
    </Group>
  );
}
